"use client";

import { Button } from "./button";
import { cn } from "@repo/utils";
import { Check, Eraser, Pen } from "lucide-react";
import * as React from "react";

interface SignaturePadProps {
  value?: string | null;
  onChange?: (signature: string | null) => void;
  className?: string;
  disabled?: boolean;
  holdDuration?: number;
}

const SignaturePad = React.forwardRef<HTMLDivElement, SignaturePadProps>(
  (
    { value, onChange, className, disabled = false, holdDuration = 1500 },
    ref,
  ) => {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = React.useState(false);
    const [hasSignature, setHasSignature] = React.useState(false);
    const [isConfirmed, setIsConfirmed] = React.useState(false);
    const [strokeColor, setStrokeColor] = React.useState("#000000");
    const [holdProgress, setHoldProgress] = React.useState(0);
    const holdTimerRef = React.useRef<ReturnType<typeof setInterval> | null>(
      null,
    );
    const holdStartRef = React.useRef<number | null>(null);

    // Detect dark mode
    React.useEffect(() => {
      const updateStrokeColor = () => {
        const isDark =
          document.documentElement.classList.contains("dark") ||
          window.matchMedia("(prefers-color-scheme: dark)").matches;
        setStrokeColor(isDark ? "#ffffff" : "#000000");
      };

      updateStrokeColor();

      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      mediaQuery.addEventListener("change", updateStrokeColor);

      const observer = new MutationObserver(updateStrokeColor);
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class"],
      });

      return () => {
        mediaQuery.removeEventListener("change", updateStrokeColor);
        observer.disconnect();
      };
    }, []);

    // Load existing signature
    React.useEffect(() => {
      if (value && canvasRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          const img = new Image();
          img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
            setHasSignature(true);
            setIsConfirmed(true);
          };
          img.src = value;
        }
      }
    }, [value]);

    const startDrawing = (
      e:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>,
    ) => {
      if (disabled || isConfirmed) return;
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      setIsDrawing(true);
      setHasSignature(true);

      const rect = canvas.getBoundingClientRect();
      const x =
        "touches" in e
          ? (e.touches[0]?.clientX || 0) - rect.left
          : e.clientX - rect.left;
      const y =
        "touches" in e
          ? (e.touches[0]?.clientY || 0) - rect.top
          : e.clientY - rect.top;

      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    };

    const draw = (
      e:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>,
    ) => {
      if (!isDrawing || disabled || isConfirmed) return;
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const rect = canvas.getBoundingClientRect();
      const x =
        "touches" in e
          ? (e.touches[0]?.clientX || 0) - rect.left
          : e.clientX - rect.left;
      const y =
        "touches" in e
          ? (e.touches[0]?.clientY || 0) - rect.top
          : e.clientY - rect.top;

      ctx.lineTo(x, y);
      ctx.stroke();
    };

    const stopDrawing = () => {
      setIsDrawing(false);
    };

    const clearSignature = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      setHasSignature(false);
      setIsConfirmed(false);
      setHoldProgress(0);
      onChange?.(null);
    };

    const startHold = () => {
      if (!hasSignature || isConfirmed || disabled) return;
      holdStartRef.current = Date.now();
      holdTimerRef.current = setInterval(() => {
        if (holdStartRef.current) {
          const elapsed = Date.now() - holdStartRef.current;
          const progress = Math.min((elapsed / holdDuration) * 100, 100);
          setHoldProgress(progress);
          if (progress >= 100) {
            confirmSignature();
          }
        }
      }, 16);
    };

    const stopHold = () => {
      if (holdTimerRef.current) {
        clearInterval(holdTimerRef.current);
        holdTimerRef.current = null;
      }
      holdStartRef.current = null;
      if (!isConfirmed) {
        setHoldProgress(0);
      }
    };

    const confirmSignature = () => {
      stopHold();
      setIsConfirmed(true);
      setHoldProgress(100);
      const canvas = canvasRef.current;
      if (canvas && onChange) {
        const dataUrl = canvas.toDataURL("image/png");
        onChange(dataUrl);
      }
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      startDrawing(e);
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      draw(e);
    };

    return (
      <div ref={ref} className={cn("space-y-2", className)}>
        <div className="relative">
          <canvas
            ref={canvasRef}
            width={400}
            height={200}
            className={cn(
              "w-full max-w-[400px] h-[200px] border border-input rounded-md bg-background touch-none",
              disabled && "opacity-50 cursor-not-allowed",
              isConfirmed && "border-green-500",
            )}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={stopDrawing}
          />
          {isConfirmed && (
            <div className="absolute top-2 left-2 flex items-center gap-1 text-green-600 text-sm">
              <Check className="size-4" />
              <span>Confirmée</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasSignature && !disabled && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clearSignature}
            >
              <Eraser className="size-4 mr-1" />
              Effacer
            </Button>
          )}
          {hasSignature && !isConfirmed && !disabled && (
            <Button
              type="button"
              variant="default"
              size="sm"
              className="relative overflow-hidden"
              onMouseDown={startHold}
              onMouseUp={stopHold}
              onMouseLeave={stopHold}
              onTouchStart={startHold}
              onTouchEnd={stopHold}
            >
              <div
                className="absolute inset-0 bg-primary-foreground/20"
                style={{ width: `${holdProgress}%` }}
              />
              <span className="relative z-10 flex items-center">
                <Check className="size-4 mr-1" />
                Maintenir pour confirmer
              </span>
            </Button>
          )}
          {!hasSignature && !disabled && (
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <Pen className="size-4" />
              Dessinez votre signature ci-dessus
            </p>
          )}
        </div>
      </div>
    );
  },
);

SignaturePad.displayName = "SignaturePad";

export { SignaturePad };
